import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import JobCard from "../components/JobCard";
import MessageThread from "../components/MessageThread";
import { toast } from "sonner";
import { Bookmark, GitCompare, ClipboardList, User, Briefcase, Trash2, MapPin, ExternalLink, MessageSquare } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "applied", label: "Applied", color: "text-blue-400 border-blue-400/40" },
  { value: "interviewing", label: "Interviewing", color: "text-yellow-500 border-yellow-500/40" },
  { value: "offer", label: "Offer", color: "text-green-500 border-green-500/40" },
  { value: "rejected", label: "Rejected", color: "text-red-500 border-red-500/40" },
  { value: "withdrawn", label: "Withdrawn", color: "text-zinc-500 border-zinc-500/40" },
];

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("saved");
  const [savedJobs, setSavedJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [selected, setSelected] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [threads, setThreads] = useState([]);
  const [openThread, setOpenThread] = useState(null);

  useEffect(() => { if (!loading && !user) navigate("/", { replace: true }); }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    setFetching(true);
    Promise.all([
      api.get("/saved-jobs").then((r) => setSavedJobs(r.data)),
      api.get("/applications").then((r) => setApps(r.data)),
      api.get("/messages/my").then((r) => setThreads(r.data.filter((t) => t.role === "apprentice"))).catch(() => {}),
    ]).finally(() => setFetching(false));
  }, [user]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) { toast.info("Compare up to 3 jobs"); return prev; }
      return [...prev, id];
    });
  };
  const compare = () => {
    if (selected.length < 2) { toast.info("Select at least 2 jobs"); return; }
    navigate(`/compare?ids=${selected.join(",")}`);
  };

  const updateStatus = async (appId, status) => {
    try {
      await api.put(`/applications/${appId}`, { status });
      setApps((prev) => prev.map((a) => a.id === appId ? { ...a, status } : a));
      toast.success("Status updated");
    } catch { toast.error("Could not update"); }
  };

  const removeApp = async (appId) => {
    try {
      await api.delete(`/applications/${appId}`);
      setApps((prev) => prev.filter((a) => a.id !== appId));
      toast.success("Removed");
    } catch { toast.error("Could not remove"); }
  };

  if (loading || !user) return <div className="max-w-7xl mx-auto p-12 text-zinc-500 font-mono-alt uppercase text-sm">Loading…</div>;

  const tabs = [
    { key: "saved", label: "Saved", count: savedJobs.length, icon: <Bookmark size={14} /> },
    { key: "applications", label: "Applications", count: apps.length, icon: <ClipboardList size={14} /> },
    { key: "messages", label: "Messages", count: threads.reduce((n, t) => n + (t.unread || 0), 0) || undefined, icon: <MessageSquare size={14} /> },
    { key: "profile", label: "Profile", icon: <User size={14} /> },
    { key: "employer", label: "Employer", icon: <Briefcase size={14} /> },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="border border-zinc-800 bg-zinc-950/50 p-8 mb-8 flex flex-col md:flex-row md:items-center gap-6">
        {user.picture && <img src={user.picture} alt="" className="w-16 h-16 border border-zinc-800" />}
        <div className="flex-1">
          <div className="text-xs font-mono-alt uppercase text-yellow-500 tracking-widest">Welcome back</div>
          <h1 className="font-display text-4xl md:text-5xl uppercase leading-none">{user.name || "Apprentice"}</h1>
          <div className="text-zinc-500 text-sm mt-1">{user.email}</div>
        </div>
      </div>

      <div className="flex flex-wrap border border-zinc-800 mb-8">
        {tabs.map((t) => (
          <button
            key={t.key}
            data-testid={`dashboard-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex-1 min-w-[140px] px-5 py-3 uppercase tracking-wider text-xs font-bold flex items-center justify-center gap-2 transition ${tab === t.key ? "bg-yellow-500 text-black" : "text-zinc-400 hover:text-white"}`}
          >
            {t.icon} {t.label} {t.count !== undefined && <span className={tab === t.key ? "text-black/70" : "text-yellow-500"}>{t.count}</span>}
          </button>
        ))}
      </div>

      {tab === "saved" && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-3xl uppercase leading-none">Saved Jobs</h2>
            {savedJobs.length >= 2 && (
              <button
                data-testid="dashboard-compare-btn"
                onClick={compare}
                disabled={selected.length < 2}
                className="border border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black uppercase font-bold tracking-wider text-sm px-6 py-3 flex items-center gap-2 disabled:opacity-40 transition"
              >
                <GitCompare size={16} /> Compare ({selected.length})
              </button>
            )}
          </div>
          {fetching ? <Loading /> : savedJobs.length === 0 ? (
            <Empty msg="Nothing saved yet" cta="Browse Jobs" to="/jobs" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {savedJobs.map((j, i) => (
                <div key={j.id} className="relative">
                  <JobCard job={j} index={i} />
                  <button
                    data-testid={`dashboard-select-${j.id}`}
                    onClick={(e) => { e.preventDefault(); toggleSelect(j.id); }}
                    className={`absolute top-3 right-3 w-8 h-8 border font-bold flex items-center justify-center text-xs transition ${selected.includes(j.id) ? "bg-yellow-500 text-black border-yellow-500" : "border-zinc-700 text-zinc-500 hover:border-yellow-500 hover:text-yellow-500"}`}
                  >{selected.includes(j.id) ? selected.indexOf(j.id) + 1 : "+"}</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "applications" && (
        <div>
          <h2 className="font-display text-3xl uppercase leading-none mb-6">Applications</h2>
          {fetching ? <Loading /> : apps.length === 0 ? (
            <Empty msg="No applications yet" cta="Browse Jobs" to="/jobs" />
          ) : (
            <div className="space-y-2">
              {apps.map((a) => (
                <div key={a.id} className="border border-zinc-800 bg-zinc-950/50 p-5" data-testid={`app-row-${a.id}`}>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="flex-1">
                      <div className="text-xs font-mono-alt uppercase text-yellow-500 tracking-widest">{a.job?.industry}</div>
                      <Link to={`/jobs/${a.job_id}`} className="font-display text-2xl uppercase leading-none hover:text-yellow-500 block mt-1">{a.job?.title || "Job"}</Link>
                      <div className="text-zinc-400 text-sm mt-1">{a.job?.employer}</div>
                      <div className="flex flex-wrap gap-3 text-xs text-zinc-500 mt-2">
                        {a.job?.location && <span className="flex items-center gap-1"><MapPin size={11} className="text-yellow-500" />{a.job.location}</span>}
                        <span className="font-mono-alt">Applied {new Date(a.applied_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        data-testid={`app-status-${a.id}`}
                        value={a.status}
                        onChange={(e) => updateStatus(a.id, e.target.value)}
                        className={`border ${STATUS_OPTIONS.find((o) => o.value === a.status)?.color || "border-zinc-700"} bg-black text-xs uppercase font-bold tracking-wider px-3 py-2 focus:outline-none`}
                      >
                        {STATUS_OPTIONS.map((o) => (<option key={o.value} value={o.value} className="bg-zinc-900 text-white">{o.label}</option>))}
                      </select>
                      {a.job?.apply_url && (
                        <a href={a.job.apply_url} target="_blank" rel="noreferrer" className="border border-zinc-800 hover:border-yellow-500 text-zinc-400 hover:text-yellow-500 p-2 transition"><ExternalLink size={14} /></a>
                      )}
                      <button
                        data-testid={`app-remove-${a.id}`}
                        onClick={() => removeApp(a.id)}
                        className="border border-zinc-800 hover:border-red-500 text-zinc-500 hover:text-red-500 p-2 transition"
                      ><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "messages" && (
        openThread ? (
          <MessageThread applicationId={openThread} onBack={() => setOpenThread(null)} />
        ) : threads.length === 0 ? (
          <Empty msg="No messages yet" cta="Browse Jobs" to="/jobs" />
        ) : (
          <div className="space-y-2">
            {threads.map((t) => (
              <button
                key={t.application_id}
                data-testid={`applicant-thread-${t.application_id}`}
                onClick={() => setOpenThread(t.application_id)}
                className="w-full text-left border border-zinc-800 hover:border-yellow-500 bg-zinc-950/50 p-4 flex items-center gap-4 transition"
              >
                {t.other_picture ? <img src={t.other_picture} alt="" className="w-10 h-10 border border-zinc-800" /> : <div className="w-10 h-10 border border-zinc-800 flex items-center justify-center"><User size={16} className="text-zinc-500" /></div>}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-display uppercase text-lg leading-none">{t.job_employer || t.other_name}</div>
                    {t.unread > 0 && <span className="bg-yellow-500 text-black text-[10px] font-bold px-2 py-0.5">{t.unread}</span>}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">RE: {t.job_title}</div>
                  {t.last_message && <div className="text-sm text-zinc-400 mt-1 truncate">{t.last_message.body}</div>}
                </div>
              </button>
            ))}
          </div>
        )
      )}

      {tab === "profile" && (
        <div>
          <h2 className="font-display text-3xl uppercase leading-none mb-3">Skill Bank Profile</h2>
          <p className="text-zinc-400 text-sm mb-4">Build once, one-click apply everywhere.</p>
          <Link to="/profile" data-testid="dashboard-profile-link" className="inline-block bg-yellow-500 text-black font-bold uppercase tracking-wider px-6 py-3 hover:bg-yellow-400 transition">
            Edit Profile
          </Link>
        </div>
      )}

      {tab === "employer" && (
        <div>
          <h2 className="font-display text-3xl uppercase leading-none mb-3">Employer Portal</h2>
          <p className="text-zinc-400 text-sm mb-4">Manage your posted jobs and analytics.</p>
          <Link to="/employer-dashboard" data-testid="dashboard-employer-link" className="inline-block bg-yellow-500 text-black font-bold uppercase tracking-wider px-6 py-3 hover:bg-yellow-400 transition">
            Open Employer Dashboard
          </Link>
        </div>
      )}
    </div>
  );
}

function Loading() { return <div className="text-zinc-500 font-mono-alt uppercase text-sm">Loading…</div>; }
function Empty({ msg, cta, to }) {
  return (
    <div className="border border-zinc-800 p-12 text-center">
      <div className="font-display text-3xl uppercase mb-2">{msg}</div>
      <Link to={to} className="inline-block bg-yellow-500 text-black font-bold uppercase tracking-wider px-6 py-3 hover:bg-yellow-400 transition mt-2">{cta}</Link>
    </div>
  );
}
